import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Upload, Camera, Loader2, Wrench, Sparkles, DollarSign, X,
  Hammer, AlertTriangle, TrendingDown, Link2,
} from "lucide-react";

const ROOM_HINTS = [
  "kitchen", "bathroom", "living_room", "bedroom",
  "exterior", "roof", "basement", "garage", "other",
];

const PRIORITY_STYLE = {
  must:   "bg-red-50 text-red-700 border-red-200",
  should: "bg-amber-50 text-amber-700 border-amber-200",
  nice:   "bg-neutral-50 text-neutral-700 border-neutral-200",
};

const COND_STYLE = {
  cosmetic: "bg-emerald-100 text-emerald-800 border-emerald-200",
  medium:   "bg-amber-100 text-amber-800 border-amber-200",
  heavy:    "bg-orange-100 text-orange-800 border-orange-200",
  gut:      "bg-red-100 text-red-800 border-red-200",
};

function money(n) {
  if (n == null) return "$0";
  return n.toLocaleString(undefined, {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  });
}

export default function RepairEstimator() {
  const [image, setImage] = useState(null);   // { name, base64, previewUrl }
  const [roomHint, setRoomHint] = useState("");
  const [sqft, setSqft] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState("");
  const fileRef = useRef(null);
  const camRef = useRef(null);

  // Load leads with an ARV so users can attach the estimate and auto-refresh MAO.
  useEffect(() => {
    api.get("/leads").then(({ data }) => {
      setLeads((data || []).filter((l) => l.property_arv));
    }).catch(() => { /* silent — attach is optional */ });
  }, []);

  const readFile = (file) => new Promise((resolve, reject) => {
    if (!file) return reject(new Error("No file"));
    if (!file.type.startsWith("image/")) return reject(new Error("Not an image"));
    if (file.size > 5 * 1024 * 1024) return reject(new Error("Image too big (max 5MB)"));
    const fr = new FileReader();
    fr.onload = () => resolve({
      name: file.name,
      base64: String(fr.result).split(",", 2)[1] || "",
      previewUrl: fr.result,
    });
    fr.onerror = () => reject(new Error("Couldn't read file"));
    fr.readAsDataURL(file);
  });

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      setImage(await readFile(f));
      setResult(null);
    } catch (err) { toast.error(err.message); }
  };

  const clearImage = () => { setImage(null); setResult(null); };

  const analyze = async () => {
    if (!image?.base64) return toast.error("Upload a photo first");
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/repair-estimator/analyze", {
        image_base64: image.base64,
        room_hint: roomHint || null,
        sqft: sqft ? Number(sqft) : null,
        lead_id: leadId || null,
      });
      setResult(data);
      if (data.mao_update) {
        toast.success(
          `MAO auto-updated → ${money(data.mao_update.new_mao)}`,
          { description: `ARV ${money(data.mao_update.arv)} × 70% − repairs ${money(data.mao_update.repairs_mid)}` },
        );
      } else {
        toast.success(`Estimate ready — ${money(data.total_high_usd)} high side`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="repair-page">
      <div className="reveal">
        <span className="app-eyebrow">/ AI Photo Repair Estimator</span>
        <h1 className="font-display text-4xl md:text-5xl mt-3 flex items-center gap-2">
          <Wrench className="text-orange-600" size={32} />
          Photo → Line-item budget
        </h1>
        <p className="text-muted-foreground mt-2 max-w-2xl text-sm">
          Snap or upload a photo of any room and get a granular, prioritized
          repair budget in seconds. Powered by GPT vision — great for quick
          walkthroughs and MAO recalculation on the fly.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-8">
        {/* LEFT: uploader / preview */}
        <div className="lg:col-span-2 space-y-4">
          <div className="card-flat p-4 space-y-3">
            {image ? (
              <div className="relative">
                <img src={image.previewUrl} alt="Preview"
                     className="w-full rounded-lg border border-[var(--app-line)]"
                     data-testid="repair-image-preview" />
                <button onClick={clearImage}
                        className="absolute top-2 right-2 rounded-full p-1.5 bg-black/70 text-white hover:bg-black"
                        data-testid="repair-image-clear">
                  <X size={12} />
                </button>
                <div className="text-[10px] text-muted-foreground mt-1 truncate">
                  {image.name}
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-[var(--app-line)] rounded-lg p-8 text-center">
                <Camera size={32} className="mx-auto text-muted-foreground/50" />
                <div className="text-xs text-muted-foreground mt-2">
                  Snap or upload a room photo
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Button variant="secondary"
                      onClick={() => fileRef.current?.click()}
                      className="rounded-full text-xs h-9"
                      data-testid="repair-upload-btn">
                <Upload size={12} className="mr-1" /> Upload
              </Button>
              <Button variant="secondary"
                      onClick={() => camRef.current?.click()}
                      className="rounded-full text-xs h-9"
                      data-testid="repair-camera-btn">
                <Camera size={12} className="mr-1" /> Camera
              </Button>
              <input ref={fileRef} type="file"
                     accept="image/jpeg,image/png,image/webp"
                     onChange={pickFile} className="hidden"
                     data-testid="repair-upload-input" />
              <input ref={camRef} type="file"
                     accept="image/*" capture="environment"
                     onChange={pickFile} className="hidden"
                     data-testid="repair-camera-input" />
            </div>
          </div>

          <div className="card-flat p-4 space-y-3">
            <div>
              <Label className="text-xs">Room type (optional)</Label>
              <select
                value={roomHint}
                onChange={(e) => setRoomHint(e.target.value)}
                className="mt-1 w-full h-10 rounded-md border border-[var(--app-line)] px-3 text-sm"
                data-testid="repair-room-select"
              >
                <option value="">Auto-detect from photo</option>
                {ROOM_HINTS.map((r) => (
                  <option key={r} value={r}>{r.replace("_", " ")}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Home sqft (optional)</Label>
              <Input type="number" value={sqft}
                     onChange={(e) => setSqft(e.target.value)}
                     placeholder="1800"
                     className="mt-1" data-testid="repair-sqft" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1">
                <Link2 size={10} /> Attach to lead (auto-refresh MAO)
              </Label>
              <select
                value={leadId}
                onChange={(e) => setLeadId(e.target.value)}
                className="mt-1 w-full h-10 rounded-md border border-[var(--app-line)] px-3 text-sm"
                data-testid="repair-lead-select"
              >
                <option value="">— Don't attach —</option>
                {leads.map((l) => (
                  <option key={l.id} value={l.id}>
                    {(l.name || "Lead")} — ARV {money(l.property_arv)}
                  </option>
                ))}
              </select>
              {leads.length === 0 && (
                <div className="text-[10px] text-muted-foreground mt-1">
                  No leads with an ARV yet. Add ARV to a lead from Property Lookup first.
                </div>
              )}
            </div>
            <Button onClick={analyze} disabled={!image || busy}
                    className="w-full rounded-full bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]"
                    data-testid="repair-analyze-btn">
              {busy
                ? <><Loader2 size={13} className="animate-spin mr-2" /> Analyzing…</>
                : <><Sparkles size={13} className="mr-2" /> Analyze photo</>}
            </Button>
          </div>
        </div>

        {/* RIGHT: results */}
        <div className="lg:col-span-3">
          {busy && !result && (
            <div className="card-flat p-8 text-center text-muted-foreground text-sm">
              <Loader2 size={24} className="mx-auto animate-spin mb-2" />
              Analyzing photo… this can take 20–40 seconds.
            </div>
          )}
          {!busy && !result && (
            <div className="card-flat p-8 text-center text-muted-foreground text-sm">
              <Hammer size={28} className="mx-auto text-muted-foreground/40 mb-2" />
              Upload a photo and hit Analyze to see a full repair breakdown here.
            </div>
          )}
          {result && (
            <div className="space-y-4" data-testid="repair-result">
              {/* MAO auto-update banner */}
              {result.mao_update && (
                <div className="rounded-lg border border-violet-300 bg-gradient-to-br from-violet-50 to-white p-4"
                     data-testid="repair-mao-banner">
                  <div className="flex items-center gap-2 text-xs font-semibold text-violet-900">
                    <TrendingDown size={14} className="text-violet-700" />
                    MAO auto-refreshed on the attached lead
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                    <div>
                      <div className="text-[10px] text-muted-foreground uppercase tracking-widest">ARV</div>
                      <div className="font-mono font-semibold">{money(result.mao_update.arv)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-muted-foreground uppercase tracking-widest">− Repairs (mid)</div>
                      <div className="font-mono font-semibold">{money(result.mao_update.repairs_mid)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-muted-foreground uppercase tracking-widest">New MAO (70% rule)</div>
                      <div className="font-mono font-bold text-violet-900"
                           data-testid="repair-new-mao">
                        {money(result.mao_update.new_mao)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Summary card */}
              <div className="card-flat p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      Detected room
                    </div>
                    <div className="font-display text-2xl font-bold capitalize"
                         data-testid="repair-room-type">
                      {(result.room_type || "unknown").replace("_", " ")}
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full border text-xs font-semibold capitalize ${
                      COND_STYLE[result.condition_label] || "bg-neutral-100"
                    }`}
                    data-testid="repair-cond-label"
                  >
                    {result.condition_label} · {result.condition_score}/100
                  </span>
                </div>
                {result.summary && (
                  <div className="text-sm mt-3 text-muted-foreground italic">
                    "{result.summary}"
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-700">
                      Low estimate
                    </div>
                    <div className="text-2xl font-display font-bold text-emerald-900"
                         data-testid="repair-total-low">
                      {money(result.total_low_usd)}
                    </div>
                  </div>
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-red-700">
                      High estimate
                    </div>
                    <div className="text-2xl font-display font-bold text-red-900"
                         data-testid="repair-total-high">
                      {money(result.total_high_usd)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Line items */}
              <div className="card-flat p-0 overflow-hidden">
                <div className="px-4 py-2 border-b border-[var(--app-line)] bg-[var(--app-surface)] text-[11px] uppercase tracking-widest text-muted-foreground">
                  Line items ({(result.line_items || []).length})
                </div>
                <div className="divide-y divide-[var(--app-line)]"
                     data-testid="repair-lines">
                  {(result.line_items || []).map((li, i) => (
                    <div key={i} className="p-3 flex items-start gap-3"
                         data-testid={`repair-line-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{li.area}</div>
                        <div className="text-xs text-muted-foreground">
                          {li.task}
                        </div>
                        <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded border ${
                          PRIORITY_STYLE[li.priority] || PRIORITY_STYLE.should
                        }`}>
                          {li.priority || "should"}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-semibold">
                          {money(li.cost_low_usd)} – {money(li.cost_high_usd)}
                        </div>
                        <div className="flex items-center justify-end gap-0.5 text-[10px] text-muted-foreground mt-0.5">
                          <DollarSign size={9} /> labor + materials
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-[10px] text-muted-foreground flex items-start gap-1">
                <AlertTriangle size={10} className="mt-0.5 shrink-0" />
                <span>
                  AI estimates only visible work. Always confirm with a
                  contractor walk-through before signing a contract.
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
